// NOTE: This file should normally not be modified unless you are adding a new provider.
// To add new routes, edit the AppRouter.tsx file.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createHead, UnheadProvider } from '@unhead/react/client';
import { InferSeoMetaPlugin } from '@unhead/addons';
import { Suspense, useEffect } from 'react';
import LightningFS from '@isomorphic-git/lightning-fs';
import NostrProvider from '@/components/NostrProvider';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NostrLoginProvider } from '@nostrify/react/login';
import { AppProvider } from '@/components/AppProvider';
import { AppConfig } from '@/contexts/AppContext';
import { AISettingsProvider } from '@/components/AISettingsProvider';
import { GitSettingsProvider } from '@/components/GitSettingsProvider';
import { DeploySettingsProvider } from '@/components/DeploySettingsProvider';
import { IntegrationsProvider } from '@/components/IntegrationsProvider';
import { SessionManagerProvider } from '@/components/SessionManagerProvider';
import { SkillsProvider } from '@/components/SkillsProvider';
import { FSProvider } from '@/components/FSProvider';
import { NWCProvider } from '@/contexts/NWCContext';
import { ConsoleErrorProvider } from '@/components/ConsoleErrorProvider';
import { LightningFSAdapter } from '@/lib/LightningFSAdapter';
import { cleanupTmpDirectory } from '@/lib/tmpCleanup';
import { DynamicFavicon } from '@/components/DynamicFavicon';

import AppRouter from './AppRouter';

const head = createHead({
  plugins: [
    InferSeoMetaPlugin(),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60000, // 1 minute
      gcTime: Infinity,
    },
  },
});

const defaultConfig: AppConfig = {
  theme: "system",
  lightVariant: "default",
  darkVariant: "default",
  relayUrls: [
    "wss://relay.ditto.pub",
    "wss://relay.nostr.band",
  ],
  projectTemplate: "https://gitlab.com/soapbox-pub/mkstack.git",
  esmUrl: "https://esm.shakespeare.diy",
  corsProxy: "https://proxy.shakespeare.diy/?url={href}",
  faviconUrl: "https://external-content.duckduckgo.com/ip3/{hostname}.ico",
  previewDomain: "local-shakespeare.dev",
  showcaseEnabled: true,
  showcaseModerator: "npub1jvnpg4c6ljadf5t6ry0w9q0rnm4mksde87kglkrc993z46c39axsgq89sc",
  ngitServers: ["git.shakespeare.diy", "relay.ngit.dev"],
};

const presetRelays = [
  { url: 'wss://relay.ditto.pub', name: 'Ditto' },
  { url: 'wss://relay.nostr.band', name: 'Nostr.Band' },
  { url: 'wss://relay.damus.io', name: 'Damus' },
  { url: 'wss://relay.primal.net', name: 'Primal' },
];

// Initialize LightningFS
const lightningFS = new LightningFS('shakespeare-fs');
const fs = new LightningFSAdapter(lightningFS.promises);

// Component to handle filesystem cleanup on startup
// Automatically removes files older than 1 hour from /tmp directory
function FSCleanupHandler() {
  useEffect(() => {
    // Run cleanup on application startup to remove stale temporary files
    // This helps prevent the VFS from accumulating old files over time
    cleanupTmpDirectory(fs).catch(console.error);
  }, []);

  return null;
}

export function App() {
  return (
    <UnheadProvider head={head}>
      <QueryClientProvider client={queryClient}>
        <AppProvider storageKey="nostr:app-config" defaultConfig={defaultConfig} presetRelays={presetRelays}>
          <FSProvider fs={fs}>
            <FSCleanupHandler />
            <ConsoleErrorProvider>
              <NostrLoginProvider storageKey='nostr:login'>
                <NostrProvider>
                  <NWCProvider>
                    <AISettingsProvider>
                    <SkillsProvider>
                      <GitSettingsProvider>
                        <DeploySettingsProvider>
                          <IntegrationsProvider>
                            <SessionManagerProvider>
                              <TooltipProvider>
                                <Toaster />
                                <DynamicFavicon />
                                <Suspense>
                                  <AppRouter />
                                </Suspense>
                              </TooltipProvider>
                            </SessionManagerProvider>
                          </IntegrationsProvider>
                        </DeploySettingsProvider>
                      </GitSettingsProvider>
                    </SkillsProvider>
                  </AISettingsProvider>
                  </NWCProvider>
                </NostrProvider>
              </NostrLoginProvider>
            </ConsoleErrorProvider>
          </FSProvider>
        </AppProvider>
      </QueryClientProvider>
    </UnheadProvider>
  );
}

export default App;
